import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Inbox, Send, Clock, MapPin } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_PILL = {
  pending: "bg-amber-soft text-amber-deep",
  confirmed: "bg-sage-100 text-sage-700",
  declined: "bg-terracotta-50 text-terracotta-700",
  maybe: "bg-ivory-100 text-ink-600",
};

function pretty(s) {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export default function Bookings() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === "booker" ? "sent" : "received");
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get(`/bookings?role=${tab}`).then(({ data }) => { if (mounted) setItems(data || []); });
    return () => { mounted = false; };
  }, [tab]);

  const grouped = useMemo(() => {
    const g = { pending: [], confirmed: [], declined: [], maybe: [] };
    items.forEach((b) => { (g[b.status] || (g[b.status] = [])).push(b); });
    return g;
  }, [items]);

  return (
    <div className="space-y-5" data-testid="bookings-page">
      <div>
        <div className="label-overline">Bookings</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Your gigs</h1>
      </div>

      <div className="flex gap-1 bg-ivory-100 rounded-full p-1 w-fit">
        {[{ k: "received", l: "Received", icon: Inbox }, { k: "sent", l: "Sent", icon: Send }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`rounded-full px-4 py-1.5 text-sm font-medium inline-flex items-center gap-2 transition ${tab === t.k ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid={`bookings-tab-${t.k}`}>
            <t.icon size={14} /> {t.l}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="card-base p-8 text-center" data-testid="bookings-empty">
          <Calendar className="mx-auto text-sage-500" size={28} />
          <div className="mt-3 font-heading font-semibold">Nothing here yet.</div>
          <p className="text-sm text-ink-600 mt-1">{tab === "received" ? "Your incoming requests will land here." : "Send a request to get the first one going."}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {["pending", "confirmed", "maybe", "declined"].map((s) => grouped[s]?.length > 0 && (
            <section key={s}>
              <h2 className="font-heading text-base font-semibold capitalize mb-2 text-ink-700">{s} ({grouped[s].length})</h2>
              <ul className="space-y-2">
                {grouped[s].map((b) => (
                  <li key={b.id}>
                    <Link to={`/bookings/${b.id}`} className="card-base p-4 hover:-translate-y-[1px] transition flex items-start justify-between gap-3" data-testid={`booking-row-${b.id}`}>
                      <div>
                        <div className="text-xs label-overline text-ink-500">{pretty(b.date)} · {b.event_type.replace("_", " ")}</div>
                        <div className="font-heading font-semibold mt-0.5">{b.ensemble_name || "AMDS booking"}</div>
                        <div className="text-sm text-ink-600 mt-0.5">
                          {tab === "received" ? `From ${b.booker_name}` : `To ${b.musician_name}`}
                          {b.venue ? <> · <span className="inline-flex items-center gap-1"><MapPin size={12} /> {b.venue}</span></> : null}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`pill ${STATUS_PILL[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>{b.status}</span>
                        <span className="text-xs text-ink-500 inline-flex items-center gap-1"><Clock size={12} /> {new Date(b.start_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
